import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ApolloClient, ObservableSubscription } from '@apollo/client';
import { priorClient } from 'modules/Apollo/priorClient';
import { GET_PRIOR_STUDY } from 'modules/Apollo/queries';
import type { GetPriorStudyQuery, GetPriorStudyQueryVariables } from 'generated/graphql';
import type { Study } from 'domains/viewer/ViewportsConfigurations/types';
import { logger } from 'modules/logger';

type ManualPriorStudy = NonNullable<GetPriorStudyQuery['study']>;

export type UseManualPriorStudiesResult = {
  studies: ReadonlyArray<Study>;
  /**
   * True until every requested smid has resolved. A smid that terminally
   * errors is excluded (see `failedSmids`) so one failure can't pin loading.
   */
  loading: boolean;
  /** Smids whose GET_PRIOR_STUDY fetch terminally errored. */
  failedSmids: ReadonlySet<string>;
  /** Clear a smid's failed state and re-attempt its fetch. */
  retry: (smid: string) => void;
};

/**
 * Fetches manually-added prior studies one at a time via `GET_PRIOR_STUDY`.
 *
 * The previous design fetched all manual priors in a single GraphQL block
 * (`studiesBySmid`) inside `GET_BASE_VIEWER_DATA`. Adding one prior changed
 * the query variables of GBVD, which caused Apollo to re-normalize the
 * entire response (main case + autoloaded priors + every existing manual
 * prior + the new one) — a multi-second synchronous block on the main
 * thread.
 *
 * Per-study fetching means:
 * - The cache write per response is bounded by ONE study's selection set.
 * - Apollo's normalized cache (`Study:{smid}`) hits on re-clicks; no rewrite.
 * - GBVD's variables stay stable when priors are added/removed, so its
 *   massive response is never re-normalized.
 *
 * The hook subscribes via `priorClient.watchQuery` per smid (rather than
 * rendering N `<useQuery>` children) so it can be used from inside another
 * hook without forcing the consumer to render a tree of hidden loaders.
 *
 * Priors are fetched through a DEDICATED Apollo client/cache (`priorClient`)
 * rather than the app's main client. This keeps prior writes out of the main
 * normalized cache so they don't broadcast to the main GET_BASE_VIEWER_DATA
 * (GBVD) watch — the broadcast that otherwise forces a multi-second main-thread
 * re-diff of the huge main-case result on every prior-add. See
 * `modules/Apollo/priorClient`.
 */
export const useManualPriorStudies = (
  priorSmids: ReadonlyArray<string>,
  client: ApolloClient<unknown> = priorClient
): UseManualPriorStudiesResult => {
  const [studyMap, setStudyMap] = useState<ReadonlyMap<string, ManualPriorStudy>>(() => new Map());
  const subscriptionsRef = useRef<Map<string, ObservableSubscription>>(new Map());
  // Smids whose fetch errored. Excluded from `loading` and NOT auto-resubscribed
  // (so a persistent failure can't loop); `retry` clears an entry to re-attempt.
  const [failedSmids, setFailedSmids] = useState<ReadonlySet<string>>(() => new Set());

  // Reconcile subscriptions whenever the set of wanted smids changes. We
  // diff by membership so that a re-ordering or duplicate doesn't tear down
  // an already-live subscription.
  useEffect(() => {
    const wanted = new Set(priorSmids);
    const subs = subscriptionsRef.current;

    // Drop subscriptions and hook-local data for smids no longer wanted.
    // Keep the session cache entry so re-adding a prior remains a cache hit.
    for (const [smid, sub] of subs) {
      if (!wanted.has(smid)) {
        sub.unsubscribe();
        subs.delete(smid);
        setStudyMap((prev) => {
          if (!prev.has(smid)) return prev;
          const next = new Map(prev);
          next.delete(smid);
          return next;
        });
      }
    }

    // Add subscriptions for new smids. `cache-first` means a re-clicked
    // prior is a synchronous cache hit with no network and no rewrite.
    for (const smid of wanted) {
      if (subs.has(smid)) continue;
      // Don't auto-retry a terminal failure; `retry()` clears it to re-attempt.
      if (failedSmids.has(smid)) continue;
      const observable = client.watchQuery<GetPriorStudyQuery, GetPriorStudyQueryVariables>({
        query: GET_PRIOR_STUDY,
        variables: { smid },
        fetchPolicy: 'cache-first',
      });
      const sub = observable.subscribe({
        next: (result) => {
          const study = result.data?.study;
          if (study == null) return;
          setStudyMap((prev) => {
            const existing = prev.get(smid);
            if (existing === study) return prev;
            const next = new Map(prev);
            next.set(smid, study);
            return next;
          });
        },
        // Per-study errors are non-fatal: mark the smid failed so it drops out
        // of `loading` and its chip returns to a clickable (retryable) state
        // instead of spinning forever. `retry()` re-attempts it.
        error: () => {
          logger.warn(`[useManualPriorStudies] failed to load prior ${smid}`);
          subs.delete(smid);
          setFailedSmids((prev) => (prev.has(smid) ? prev : new Set(prev).add(smid)));
        },
      });
      subs.set(smid, sub);
    }
    // Forget failures for smids no longer requested, so re-adding one (or a
    // future click) fetches fresh rather than staying stuck as "failed".
    setFailedSmids((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set<string>();
      for (const smid of prev) {
        if (wanted.has(smid)) next.add(smid);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [priorSmids, client, failedSmids]);

  // Tear down all subscriptions when the consuming hook unmounts.
  useEffect(() => {
    const subs = subscriptionsRef.current;
    return () => {
      for (const sub of subs.values()) sub.unsubscribe();
      subs.clear();
    };
  }, []);

  const studies = useMemo<ReadonlyArray<Study>>(
    () =>
      priorSmids
        .map((smid) => studyMap.get(smid))
        .filter((study): study is ManualPriorStudy => study != null) as ReadonlyArray<Study>,
    [priorSmids, studyMap]
  );

  const loading = useMemo(
    () => priorSmids.some((smid) => !studyMap.has(smid) && !failedSmids.has(smid)),
    [priorSmids, studyMap, failedSmids]
  );

  const retry = useCallback((smid: string) => {
    setFailedSmids((prev) => {
      if (!prev.has(smid)) return prev;
      const next = new Set(prev);
      next.delete(smid);
      return next;
    });
  }, []);

  return { studies, loading, failedSmids, retry };
};
